import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, Printer } from "lucide-react";
import { api } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { todayISO } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";
import { cn } from "@/lib/utils";

const money = (v) => {
  const n = Number(v || 0);
  const s = Math.abs(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n < 0 ? `(${s})` : s;
};
const fyStart = () => {
  const t = new Date();
  const y = t.getMonth() >= 3 ? t.getFullYear() : t.getFullYear() - 1;
  return `${y}-04-01`;
};
const MODES = [["with", "With GST"], ["without", "Without GST"], ["both", "Both"]];

export default function FinanceSummary() {
  const { companies } = useCompany() || {};
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("all");
  const [from, setFrom] = useState(fyStart());
  const [to, setTo] = useState(todayISO());
  const [mode, setMode] = useState("both");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/masters/branches").then(({ data }) => setBranches(data || [])).catch(() => {}); }, []);
  const multiCompany = (companies || []).length > 1;
  const branchLabel = (b) => (multiCompany ? `${b.name} (${(companies || []).find((c) => c.id === b.company_id)?.name || "—"})` : b.name);

  // all = every branch added up; a branch = only what is filed under it (the top-bar branch doesn't narrow this report)
  const run = async (forBranch = branchId) => {
    setLoading(true);
    try {
      const params = { date_from: from, date_to: to };
      if (forBranch !== "all") params.branch_id = forBranch;
      const { data: d } = await api.get("/reports/finance-summary", { params });
      setData(d);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const chosen = branches.find((b) => b.id === branchId);
  const companyName = branchId === "all" ? "All branches" : chosen ? branchLabel(chosen) : "";
  // columns shown for the chosen mode: [key, heading]
  const cols = mode === "with" ? [["total", "With GST"]] : mode === "without" ? [["taxable", "Without GST"]] : [["taxable", "Without GST"], ["gst", "GST"], ["total", "With GST"]];

  const rows = useMemo(() => !data ? [] : [
    { label: "Billed to clients (RA bills)", note: `${data.sales.count} bills`, v: data.sales, tone: "in" },
    { label: "Material purchases", note: `${data.purchases.count} bills`, v: data.purchases },
    { label: "Less: debit notes", note: `${data.debit_notes.count} notes`, v: neg(data.debit_notes) },
    { label: "Subcontractor bills", note: `${data.subcontract.count} bills`, v: data.subcontract },
    { label: "Site expenses (wages, fuel, petty cash)", note: `${data.site_expenses.count} entries`, v: data.site_expenses },
  ], [data]);

  const downloadCsv = () => {
    if (!data) return;
    const lines = [["Finance Summary", companyName, `${data.date_from} to ${data.date_to}`], [], ["Item", "Without GST", "GST", "With GST"]];
    rows.forEach((r) => lines.push([r.label, r.v.taxable, r.v.gst, r.v.total]));
    lines.push(["Total spent", data.spent.taxable, data.spent.gst, data.spent.total]);
    lines.push(["Profit (billed - spent)", data.profit.taxable, data.profit.gst, data.profit.total]);
    lines.push([], ["GST collected on sales", data.gst.output], ["GST paid on purchases and expenses", data.gst.input], ["Net GST payable", data.gst.net_payable]);
    lines.push([], ["Received from clients", data.cash.received], ["Paid to vendors", data.cash.paid], ["Clients still owe us", data.cash.receivable], ["We still owe vendors", data.cash.payable]);
    lines.push([], ["Project", "Billed w/o GST", "Billed GST", "Spent w/o GST", "Spent GST"]);
    data.projects.forEach((p) => lines.push([`${p.code} ${p.name}`, p.billed_taxable, p.billed_gst, p.spent_taxable, p.spent_gst]));
    const csv = lines.map((l) => l.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `finance-summary-${data.date_from}-to-${data.date_to}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const big = (b) => (mode === "without" ? b.taxable : b.total);

  return (
    <div className="space-y-5" data-testid="finance-summary-page">
      <div className="no-print">
        <PageHeader
          title="Finance Summary"
          description="Billed, spent, profit, GST, cash and outstanding — with and without GST. Print it or save it as PDF."
          action={data && (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={downloadCsv} data-testid="finance-summary-csv"><Download className="h-3.5 w-3.5" /> CSV</Button>
              <Button size="sm" onClick={() => window.print()} data-testid="finance-summary-print"><Printer className="h-3.5 w-3.5" /> Print / PDF</Button>
            </div>
          )}
        />
        <div className="mt-4 flex flex-wrap items-end gap-3 rounded-xl border border-border bg-card p-3">
          <div>
            <p className="mb-1.5 text-xs text-muted-foreground">Branch</p>
            <Select value={branchId} onValueChange={(v) => { setBranchId(v); run(v); }}>
              <SelectTrigger className="w-52" data-testid="finance-summary-branch"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All branches</SelectItem>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{branchLabel(b)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><p className="mb-1.5 text-xs text-muted-foreground">From</p><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" /></div>
          <div><p className="mb-1.5 text-xs text-muted-foreground">To</p><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" /></div>
          <div>
            <p className="mb-1.5 text-xs text-muted-foreground">Show</p>
            <div className="inline-flex rounded-lg border border-border bg-muted p-1">
              {MODES.map(([v, label]) => (
                <button key={v} type="button" onClick={() => setMode(v)} data-testid={`finance-summary-mode-${v}`}
                  className={cn("rounded-md px-3 py-1.5 text-sm font-medium", mode === v ? "bg-card text-foreground shadow-sm" : "text-muted-foreground")}>{label}</button>
              ))}
            </div>
          </div>
          <Button onClick={() => run()} disabled={loading} data-testid="finance-summary-run">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Run report"}</Button>
        </div>
      </div>

      {!data ? (
        <div className="no-print"><EmptyState title="Run the report" description="Pick the branch and dates, then click Run report." testId="finance-summary-empty" /></div>
      ) : (
        <div className="space-y-5" id="finance-summary-print">
          <div className="print-only">
            <h1 className="text-xl font-bold">Finance Summary</h1>
            <p className="text-sm">{companyName} · {data.date_from} to {data.date_to} · {MODES.find((m) => m[0] === mode)[1]}</p>
            <p className="text-xs">Printed on {todayISO()}</p>
          </div>

          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Card label="Billed to clients" value={big(data.sales)} sub={mode === "both" ? `With GST ${money(data.sales.total)}` : undefined} />
            <Card label="Total spent" value={big(data.spent)} sub={mode === "both" ? `With GST ${money(data.spent.total)}` : undefined} />
            <Card label="Profit" value={big(data.profit)} sub={data.margin_pct ? `${data.margin_pct}% margin (without GST)` : undefined} tone={Number(big(data.profit)) < 0 ? "bad" : "good"} />
            <Card label={Number(data.gst.net_payable) >= 0 ? "GST to pay" : "GST refundable"} value={String(Math.abs(Number(data.gst.net_payable)))} sub="Collected − paid" />
          </div>

          <Block title="Billed and spent">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                  <th className="py-2 pr-3">Item</th>{cols.map(([k, h]) => <th key={k} className="px-3 text-right">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.label} className="border-b border-border/50">
                    <td className="py-2 pr-3"><span className="text-foreground">{r.label}</span> <span className="text-xs text-muted-foreground">· {r.note}</span></td>
                    {cols.map(([k]) => <td key={k} className="px-3 text-right tabular-nums">{money(r.v[k])}</td>)}
                  </tr>
                ))}
                <tr className="border-b border-border font-semibold"><td className="py-2">Total spent</td>{cols.map(([k]) => <td key={k} className="px-3 text-right tabular-nums">{money(data.spent[k])}</td>)}</tr>
                <tr className="font-semibold"><td className="py-2">Profit (billed − spent)</td>{cols.map(([k]) => <td key={k} className={cn("px-3 text-right tabular-nums", Number(data.profit[k]) < 0 && "text-rose-500")}>{money(data.profit[k])}</td>)}</tr>
              </tbody>
            </table>
          </Block>

          <div className="grid gap-5 lg:grid-cols-2">
            <Block title="GST position">
              <Lines items={[["GST collected on sales", data.gst.output], ["GST paid on purchases, subcontract & expenses", data.gst.input], [Number(data.gst.net_payable) >= 0 ? "Net GST payable" : "Net GST refundable", String(Math.abs(Number(data.gst.net_payable))), true]]} />
              <p className="mt-2 text-xs text-muted-foreground">Based on the GST on posted bills in this period. Your GST return is the final word.</p>
            </Block>
            <Block title="Cash and outstanding">
              <Lines items={[["Received from clients (period)", data.cash.received], ["Paid to vendors (period)", data.cash.paid], ["Clients still owe us (today)", data.cash.receivable], ["We still owe vendors (today)", data.cash.payable]]} />
            </Block>
          </div>

          <Block title="By project">
            {data.projects.length === 0 ? <p className="text-sm text-muted-foreground">No project activity in this period.</p> : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                    <th className="py-2 pr-3">Project</th>
                    {cols.map(([k, h]) => <th key={`b${k}`} className="px-3 text-right">Billed · {h}</th>)}
                    {cols.map(([k, h]) => <th key={`s${k}`} className="px-3 text-right">Spent · {h}</th>)}
                    {cols.filter(([k]) => k !== "gst").map(([k, h]) => <th key={`p${k}`} className="px-3 text-right">Profit · {h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {data.projects.map((p) => {
                    const bw = { taxable: Number(p.billed_taxable), gst: Number(p.billed_gst), total: Number(p.billed_taxable) + Number(p.billed_gst) };
                    const sw = { taxable: Number(p.spent_taxable), gst: Number(p.spent_gst), total: Number(p.spent_taxable) + Number(p.spent_gst) };
                    return (
                      <tr key={p.id} className="border-b border-border/50">
                        <td className="py-2 pr-3 text-foreground">{p.name} <span className="font-mono text-xs text-muted-foreground">{p.code}</span></td>
                        {cols.map(([k]) => <td key={`b${k}`} className="px-3 text-right tabular-nums">{money(bw[k])}</td>)}
                        {cols.map(([k]) => <td key={`s${k}`} className="px-3 text-right tabular-nums">{money(sw[k])}</td>)}
                        {cols.filter(([k]) => k !== "gst").map(([k]) => <td key={`p${k}`} className={cn("px-3 text-right tabular-nums", bw[k] - sw[k] < 0 && "text-rose-500")}>{money(bw[k] - sw[k])}</td>)}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Block>

          <p className="text-xs text-muted-foreground">
            This summary is built from posted bills, payments and expenses. It is not the accounting Profit & Loss — for that, use Financial Statements.
            Without GST = taxable value. With GST = taxable value + GST.
          </p>
        </div>
      )}
    </div>
  );
}

function neg(b) {
  const f = (x) => String(-Number(x));
  return { ...b, taxable: f(b.taxable), gst: f(b.gst), total: f(b.total) };
}

function Card({ label, value, sub, tone }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={cn("mt-1 text-2xl font-semibold tabular-nums", tone === "bad" && "text-rose-500", tone === "good" && "text-emerald-600")}>₹{money(value)}</p>
      {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

function Block({ title, children }) {
  return (
    <div className="print-block overflow-x-auto rounded-xl border border-border bg-card p-4">
      <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</p>
      {children}
    </div>
  );
}

function Lines({ items }) {
  return (
    <div className="space-y-1.5 text-sm">
      {items.map(([label, value, strong]) => (
        <div key={label} className={cn("flex justify-between gap-3", strong && "border-t border-border pt-1.5 font-semibold")}>
          <span className="text-muted-foreground">{label}</span><span className="tabular-nums text-foreground">₹{money(value)}</span>
        </div>
      ))}
    </div>
  );
}
